import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { track } from '@/lib/analytics'
import { emitPollEvent } from '@/api/pollEvents'
import { fetchPollsList } from '@/api/polls'
import { PollsWizardShell } from '@/onboarding/PollsWizardShell'
import { usePollsWizard, POLL_WIZARD_TEMPLATES, type PollWizardTemplateId } from '@/onboarding/PollsWizardContext'
import { clearPollsFlow } from '@/onboarding/pollsFlow'

/**
 * Slide 18 · «С чего спросим аудиторию?» — шаг 1/3 polls-onboarding wizard'а.
 * 4 template-карточки → prefill state → nav на /preview.
 *
 * activePolls > 0 → hard-redirect на /polls (Виктор 2026-07-28: юзер должен
 * видеть свои live-опросы, не «Создай ещё один»). `?debug=wizard` — bypass.
 */
export function PollsWizardTemplatePage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { state, setTemplate, setChannelUUID, setRef } = usePollsWizard()

  const channelUUID = params.get('channelUUID')
  const ref = params.get('ref')

  useEffect(() => {
    if (channelUUID) setChannelUUID(channelUUID)
    if (ref) setRef(ref)
    clearPollsFlow()
    if (ref) {
      track('poll_wizard_opened', { step: 'template', ref })
      emitPollEvent('poll_onboarding_opened', {
        pollId: ref,
        meta: { source: 'lk', entryPath: '/onboarding/polls/template', ref },
      })
    }
    if (!channelUUID) return
    if (params.get('debug') === 'wizard') return
    let cancelled = false
    void (async () => {
      const list = await fetchPollsList({ channelUUID })
      if (cancelled || !list) return
      if (list.kpi.activePolls > 0) {
        const qs = new URLSearchParams({ channelUUID })
        if (ref) qs.set('ref', ref)
        navigate(`/polls?${qs.toString()}`, { replace: true })
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePick = (id: PollWizardTemplateId) => {
    setTemplate(id)
    track('poll_wizard_template_picked', { template: id, ref })
    emitPollEvent('poll_onboarding_step_completed', {
      pollId: ref ?? undefined,
      meta: {
        source: 'lk',
        step: 'template_picked',
        ref: ref ?? null,
        channelUUID: channelUUID ?? state.channelUUID ?? null,
        template: id,
      },
    })
    const qs = new URLSearchParams()
    if (channelUUID) qs.set('channelUUID', channelUUID)
    if (ref) qs.set('ref', ref)
    navigate(`/onboarding/polls/preview?${qs.toString()}`)
  }

  return (
    <PollsWizardShell step={1} total={3}>
      <div className="pw-narrow">
        <p className="pw-eyebrow-line">
          <span className="pw-eyebrow-pill">Опросы</span>
          Быстрый старт
        </p>
        <h1 className="pw-title">
          Что <em>спросим</em> у&nbsp;аудитории?
        </h1>
        <p className="pw-sub">
          Выберите готовый шаблон — мы&nbsp;подставим вопрос и&nbsp;варианты. Всё&nbsp;можно поправить перед публикацией.
        </p>

        <div className="tpl-grid">
          {TEMPLATE_META.map((meta) => {
            const tpl = POLL_WIZARD_TEMPLATES[meta.id]
            return (
              <button
                key={meta.id}
                className={`tpl-card ${state.templateId === meta.id ? 'on' : ''}`}
                type="button"
                onClick={() => handlePick(meta.id)}
              >
                <div className="tpl-ic" aria-hidden>{meta.icon}</div>
                <div className="tpl-name">{meta.name}</div>
                <div className="tpl-desc">«{tpl.question}»&nbsp;— {meta.hint}</div>
                <div className="tpl-meta">{meta.metaLine}</div>
              </button>
            )
          })}
        </div>
      </div>
      <PageStyles />
    </PollsWizardShell>
  )
}

const TEMPLATE_META: {
  id: PollWizardTemplateId
  icon: string
  name: string
  hint: string
  metaLine: string
}[] = [
  { id: 'pulse', icon: '📈', name: 'Пульс канала', hint: 'померить настроение аудитории. Классика для старта.', metaLine: 'одиночный выбор · 4 варианта' },
  { id: 'topic', icon: '📝', name: 'Что публиковать дальше', hint: 'пусть аудитория сама подскажет тему.', metaLine: 'одиночный выбор · 3–5 вариантов' },
  { id: 'multi', icon: '✅', name: 'Несколько ответов', hint: 'можно выбрать несколько вариантов сразу.', metaLine: 'множественный выбор' },
  { id: 'quiz', icon: '🏆', name: 'Викторина', hint: 'один правильный ответ и реакция бота. Вовлекает и развлекает.', metaLine: 'quiz · правильный ответ' },
]

function PageStyles() {
  return (
    <style>{`
      .pw-narrow { max-width: 780px; width: 100%; min-width: 0; }
      .pw-eyebrow-line {
        display: flex; align-items: center; gap: 10px;
        font-size: 12.5px; color: var(--tg-mute, #8f9eb0); font-weight: 500;
        margin: 0 0 14px;
      }
      .pw-eyebrow-pill {
        display: inline-flex; align-items: center;
        padding: 4px 10px; border-radius: 999px;
        background: var(--tg-blue-bg, #ebf2ff);
        color: var(--tg-blue-press, #0052cc);
        font-weight: 700; font-size: 11px; letter-spacing: 0.06em;
        text-transform: uppercase;
      }
      .pw-title {
        font-weight: 800; font-size: 36px; line-height: 1.05;
        letter-spacing: -0.026em; color: var(--tg-ink, #111);
        margin: 0 0 8px;
      }
      .pw-title em { font-style: italic; font-weight: 400; color: var(--tg-blue, #0066ff); }
      .pw-sub {
        font-size: 14.5px; color: var(--tg-mute, #8f9eb0); margin: 0 0 28px;
        line-height: 1.5;
      }

      .tpl-grid {
        display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px;
      }
      @media (max-width: 700px) { .tpl-grid { grid-template-columns: 1fr; } }
      .tpl-card {
        text-align: left; padding: 18px;
        background: var(--tg-surface, #fff);
        border: 1px solid var(--tg-line-2, #f0f0f4);
        border-radius: 14px;
        cursor: pointer; font-family: inherit;
        transition: all 160ms;
        width: 100%;
        min-width: 0;
        box-sizing: border-box;
      }
      .tpl-card:hover {
        border-color: var(--tg-blue-soft, rgba(0,128,255,0.4));
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.06);
      }
      .tpl-card.on {
        border-color: var(--tg-blue, #0066ff);
        background: var(--tg-blue-bg, #ebf2ff);
      }
      .tpl-ic { font-size: 22px; margin-bottom: 10px; line-height: 1; }
      .tpl-name { font-weight: 700; font-size: 15px; color: var(--tg-ink, #111); margin-bottom: 6px; overflow-wrap: anywhere; }
      .tpl-desc { font-size: 13px; color: var(--tg-mute, #8f9eb0); line-height: 1.4; margin-bottom: 8px; overflow-wrap: anywhere; }
      .tpl-meta {
        font-size: 11px; color: var(--tg-mute-2, #c4c4c8);
        letter-spacing: 0.04em; text-transform: uppercase; font-weight: 600;
        overflow-wrap: anywhere;
      }
      @media (max-width: 700px) {
        .pw-title { font-size: 30px; }
        .pw-sub { margin-bottom: 20px; }
        .tpl-card { padding: 16px; }
      }

    `}</style>
  )
}
